/**
 * 点云渲染后端 —— 封装基础渲染策略与可选 EDL 后处理
 *
 * 职责：
 * 1. 接收 PointCloudPlugin 委托，按基础策略与 EDL 状态选择渲染路径
 * 2. 管理私有 RenderTarget（EDL / HQ Splat），不暴露给外部
 * 3. 遵守 RT 进出协议：完成后恢复 renderer.setRenderTarget(outputTarget)
 * 4. 不依赖 Viewer 级状态，所有输入通过构造参数或 render 参数传入
 */

import * as THREE from 'three'
import type { PointCloudOctree } from '@/plugins/pointCloud/core/PointCloudOctree'
import { createOrthographicRenderCamera } from '@/plugins/pointCloud/rendering/createOrthographicRenderCamera'
import type {
  IPointCloudPrivateRenderer,
  PointCloudPrivateRendererCapabilities,
  PointCloudPrivateRendererDiagnostics,
  PointCloudPrivateRendererPickRawResult,
  PointCloudPrivateRendererPickRequest,
} from '@/plugins/pointCloud/rendering/PointCloudPrivateRenderer'
import type { PotreeRenderer } from '@/plugins/pointCloud/rendering/PotreeRenderer'
import { PointShape } from '@/shared/constants'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { ViewFrameContext } from '@/shared/types/view'
import { EyeDomeLightingMaterial } from './materials/EyeDomeLightingMaterial'
import { NormalizationEDLMaterial } from './materials/NormalizationEDLMaterial'
import { NormalizationMaterial } from './materials/NormalizationMaterial'
import { PointCloudMaterial } from './materials/PointCloudMaterial'
import type { PointCloudRenderConfig } from './types'
import { screenPass } from './utils/geometry-utils'

// ==================== 类型定义 ====================

/** 获取可见八叉树列表的回调 */
type OctreeProvider = () => PointCloudOctree[]

/** 获取渲染配置的回调 */
type ConfigProvider = () => PointCloudRenderConfig

type RenderSize = {
  width: number
  height: number
}

export type PointCloudRenderBackendMemoryDiagnostics = PointCloudPrivateRendererDiagnostics

const WEBGL2_CAPABILITIES: Readonly<PointCloudPrivateRendererCapabilities> = Object.freeze({
  edl: true,
  hqSplat: true,
  gpuPick: true,
})

// ==================== PointCloudRenderBackend ====================

/** 点云渲染后端，封装 default / EDL / HQ Splat 三种渲染策略 */
export class PointCloudRenderBackend implements IPointCloudPrivateRenderer {
  /** 当前实现固定为 WebGL2。 */
  readonly backend = 'webgl2' as const

  /** 当前 WebGL2 路径已实现的能力。 */
  readonly capabilities = WEBGL2_CAPABILITIES

  /** 底层 WebGL 点云渲染器 */
  private readonly pRenderer: PotreeRenderer

  /** 获取渲染配置 */
  private readonly getConfig: ConfigProvider

  /** 获取可见八叉树列表 */
  private readonly getVisibleOctrees: OctreeProvider

  // ─── EDL 私有资源 ─────────────────────────────────────

  /** EDL 材质（懒初始化） */
  private edlMaterial: EyeDomeLightingMaterial | null = null

  /** EDL 颜色+深度 RT */
  private rtEDL: THREE.WebGLRenderTarget | null = null

  // ─── HQ Splat 私有资源 ─────────────────────────────────

  /** HQ Splat 归一化材质 */
  private normalizationMaterial: NormalizationMaterial | null = null

  /** HQ Splat 归一化 EDL 材质 */
  private normalizationEDLMaterial: NormalizationEDLMaterial | null = null

  /** HQ Splat 深度 RT */
  private rtDepth: THREE.WebGLRenderTarget | null = null

  /** HQ Splat 属性 RT */
  private rtAttribute: THREE.WebGLRenderTarget | null = null

  /** HQ Splat 深度材质缓存（按八叉树） */
  private depthMaterials = new Map<PointCloudOctree, PointCloudMaterial>()

  /** HQ Splat 属性材质缓存（按八叉树） */
  private attributeMaterials = new Map<PointCloudOctree, PointCloudMaterial>()

  /** 当前后端实际使用过 screen pass 的 Renderer，dispose 时逐一释放。 */
  private readonly screenPassRenderers = new Set<THREE.WebGLRenderer>()
  private disposed = false

  constructor(
    pRenderer: PotreeRenderer,
    getConfig: ConfigProvider,
    getVisibleOctrees: OctreeProvider
  ) {
    this.pRenderer = pRenderer
    this.getConfig = getConfig
    this.getVisibleOctrees = getVisibleOctrees
  }

  // ─── 公共接口 ──────────────────────────────────────────

  /**
   * 渲染入口，由 PointCloudPlugin.render() 委托调用。
   *
   * @param frame - 当前视图帧上下文
   * @param phase - 当前渲染阶段
   * @param ctx - 阶段级渲染上下文
   */
  render(frame: ViewFrameContext, phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (phase === RenderPhase.Prepare) {
      this._prepare(frame, ctx)
    } else if (phase === RenderPhase.MainOpaque) {
      this._renderMainOpaque(frame, ctx)
    }
  }

  /** 使用 WebGL2 pick target 执行一次插件私有 GPU 拾取。 */
  pick(
    request: PointCloudPrivateRendererPickRequest
  ): PointCloudPrivateRendererPickRawResult | null {
    if (this.disposed) {
      return null
    }

    return request.octree.pick(
      this.pRenderer.threeRenderer,
      this.pRenderer,
      request.camera,
      request.ray,
      {
        pickWindowSize: request.pickWindowSize,
        pickClipped: true,
        width: request.width,
        height: request.height,
        x: request.x,
        y: request.y,
        minDistance: request.minDistance,
      }
    )
  }

  /** 获取后端私有缓存数量，不创建新资源。 */
  getMemoryDiagnostics(): PointCloudRenderBackendMemoryDiagnostics {
    return {
      backend: this.backend,
      hqDepthMaterials: this.depthMaterials.size,
      hqAttributeMaterials: this.attributeMaterials.size,
      hasEdlMaterial: this.edlMaterial !== null,
      hasEdlTarget: this.rtEDL !== null,
      hasDepthTarget: this.rtDepth !== null,
      hasAttributeTarget: this.rtAttribute !== null,
    }
  }

  /** 释放所有私有资源 */
  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this._disposeEDLResources()
    this._disposeHQResources()
    for (const renderer of this.screenPassRenderers) {
      screenPass.release?.(renderer)
    }
    this.screenPassRenderers.clear()
    this.pRenderer.dispose()
  }

  private _retainScreenPassRenderer(renderer: THREE.WebGLRenderer): void {
    if (this.screenPassRenderers.has(renderer)) {
      return
    }
    this.screenPassRenderers.add(renderer)
    screenPass.retain?.(renderer)
  }

  private _bindOutputRect(
    renderer: THREE.WebGLRenderer,
    outputTarget: THREE.WebGLRenderTarget | null,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    renderer.setRenderTarget(outputTarget)
    renderer.setViewport(x, y, width, height)
    renderer.setScissor(x, y, width, height)
    renderer.setScissorTest(true)
  }

  private _bindViewOutput(
    renderer: THREE.WebGLRenderer,
    outputTarget: THREE.WebGLRenderTarget | null,
    viewport: PhaseRenderContext['viewport']
  ): void {
    this._bindOutputRect(
      renderer,
      outputTarget,
      viewport.x,
      viewport.y,
      viewport.width,
      viewport.height
    )
  }

  private _bindFullTarget(
    renderer: THREE.WebGLRenderer,
    outputTarget: THREE.WebGLRenderTarget | null,
    width: number,
    height: number
  ): void {
    renderer.setRenderTarget(outputTarget)
    const gl = renderer.getContext() as WebGL2RenderingContext
    gl.viewport(0, 0, width, height)
    gl.scissor(0, 0, width, height)
    gl.enable(gl.SCISSOR_TEST)
  }

  private _toDeviceViewport(
    renderer: THREE.WebGLRenderer,
    viewport: PhaseRenderContext['viewport']
  ): PhaseRenderContext['viewport'] {
    const pixelRatio = Math.max(renderer.getPixelRatio?.() ?? 1, 1)
    return {
      x: Math.round(viewport.x * pixelRatio),
      y: Math.round(viewport.y * pixelRatio),
      width: Math.round(viewport.width * pixelRatio),
      height: Math.round(viewport.height * pixelRatio),
    }
  }

  private _getPrivateRenderSize(
    renderer: THREE.WebGLRenderer,
    viewport: PhaseRenderContext['viewport']
  ): RenderSize {
    const pixelRatio = Math.max(renderer.getPixelRatio?.() ?? 1, 1)
    return {
      width: Math.max(Math.round(viewport.width * pixelRatio), 1),
      height: Math.max(Math.round(viewport.height * pixelRatio), 1),
    }
  }

  // ─── Prepare 阶段 ─────────────────────────────────────

  /** 准备阶段：更新材质的屏幕尺寸等参数 */
  private _prepare(_frame: ViewFrameContext, ctx: PhaseRenderContext): void {
    const { viewport } = ctx
    const octrees = this.getVisibleOctrees()

    for (const octree of octrees) {
      const material = octree.material
      material.screenWidth = viewport.width
      material.screenHeight = viewport.height

      // 同步八叉树几何信息到材质
      const octreeSize = octree.pcoGeometry?.boundingBox?.getSize(new THREE.Vector3()).x ?? 0
      material.uniforms.octreeSize.value = octreeSize
      material.uniforms.visibleNodes.value = material.visibleNodesTexture
      material.spacing = octree.pcoGeometry.spacing
    }
  }

  // ─── MainOpaque 阶段 ──────────────────────────────────

  /** 主渲染阶段：按基础策略与 EDL 状态分发到具体渲染路径 */
  private _renderMainOpaque(frame: ViewFrameContext, ctx: PhaseRenderContext): void {
    const octrees = this.getVisibleOctrees()
    // pointCloudAndGSplat 等组合预设即使没有加载点云也会注册本后端。空资产时必须保持
    // 上游已绘制的 scene.background，不能进入默认路径把最终颜色缓冲重新清成黑色。
    if (octrees.length === 0) {
      return
    }

    const config = this.getConfig()
    const camera = frame.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
    const clearColor = ctx.renderer.getClearColor(new THREE.Color())
    const clearAlpha = ctx.renderer.getClearAlpha()

    // PotreeRenderer bypasses THREE.WebGLRenderer.render(scene, camera), so we
    // must refresh camera/object world matrices ourselves before reading them.
    camera.updateMatrixWorld(true)
    for (const octree of octrees) {
      octree.updateMatrixWorld(true)
    }

    const renderFrame =
      camera instanceof THREE.OrthographicCamera
        ? { ...frame, camera: createOrthographicRenderCamera(camera) }
        : frame

    try {
      if (config.renderStrategy === 'hq-splat') {
        this._renderHQSplat(renderFrame, ctx, config)
      } else if (config.edl.enabled) {
        this._renderEDL(renderFrame, ctx, config)
      } else {
        this._renderDefault(renderFrame, ctx, config)
      }
    } finally {
      // 三条路径内部均会调用 PotreeRenderer.renderOctree 直接写入原生 GL 状态，
      // 会污染 Three 的 program/material 缓存。在公共出口统一 resetState，
      // 避免后续 ViewHelper 等 sprite overlay 被遮挡，并恢复共享 renderer 的清屏状态。
      ctx.renderer.resetState()
      ctx.renderer.setClearColor(clearColor, clearAlpha)
    }
  }

  // ─── Default 渲染路径 ─────────────────────────────────

  /** 默认渲染：直接遍历可见八叉树，调用 pRenderer.renderOctree 到 outputTarget */
  private _renderDefault(
    frame: ViewFrameContext,
    ctx: PhaseRenderContext,
    _config: PointCloudRenderConfig
  ): void {
    const { renderer, outputTarget, viewport } = ctx
    const camera = frame.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
    const octrees = this.getVisibleOctrees()

    for (const octree of octrees) {
      octree.material.weighted = false
      octree.material.useEDL = false
    }

    // Reset Three.js tracked GL state before entering the raw Potree path.
    // We intentionally do this before rendering, not after rendering.
    renderer.resetState()
    this._bindViewOutput(renderer, outputTarget, viewport)

    // 用原始 GL 清屏，避免 Three.js 内部状态干扰。
    //
    // 共享输出（outputTarget === null）的颜色已由 RenderLoop 的视口清屏 + 背景绘制完成，这里
    // 只能清深度：再清一次颜色会擦掉同一帧上游插件的像素，也会覆盖 `scene.background`
    // （参见 docs/architecture/plugin-architecture-detail.md §6.7.1 与
    // migration-evidence.md §14.13-A）。离屏 target（区域采集等）仍需清颜色，避免沿用上一帧内容。
    const gl = renderer.getContext() as WebGL2RenderingContext
    const deviceViewport = this._toDeviceViewport(renderer, viewport)
    gl.enable(gl.SCISSOR_TEST)
    gl.scissor(deviceViewport.x, deviceViewport.y, deviceViewport.width, deviceViewport.height)
    if (outputTarget) {
      gl.clearColor(0, 0, 0, 1)
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    } else {
      gl.clear(gl.DEPTH_BUFFER_BIT)
    }

    // 逐八叉树渲染可见节点
    for (const octree of octrees) {
      const visibleNodes = octree.visibleNodes
      if (visibleNodes.length === 0) continue

      this.pRenderer.renderOctree(octree, visibleNodes, camera, outputTarget)
    }

    // For offscreen RTs we restore the target explicitly.
    // When drawing directly to the default framebuffer, rebinding null here can
    // interfere with the just-produced raw GL output on some paths.
    if (outputTarget) {
      renderer.setRenderTarget(outputTarget)
    }
  }

  // ─── EDL 渲染路径 ──────────────────────────────────────

  /** 懒初始化 EDL 资源 */
  private _initEDL(): void {
    if (this.edlMaterial) return

    this.edlMaterial = new EyeDomeLightingMaterial({
      glslVersion: THREE.GLSL3,
    })
    // Always accept surviving point fragments and write their sampled scene depth.
    this.edlMaterial.depthTest = true
    this.edlMaterial.depthWrite = true
    this.edlMaterial.depthFunc = THREE.AlwaysDepth
    this.edlMaterial.transparent = true

    this.rtEDL = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthTexture: new THREE.DepthTexture(undefined, undefined, THREE.UnsignedIntType),
    })
  }

  /** EDL 渲染：先渲染点云到私有 rtEDL，再做 EDL 后处理到 outputTarget */
  private _renderEDL(
    frame: ViewFrameContext,
    ctx: PhaseRenderContext,
    config: PointCloudRenderConfig
  ): void {
    this._initEDL()

    const { renderer, outputTarget, viewport } = ctx
    const camera = frame.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
    const octrees = this.getVisibleOctrees()
    const { width, height } = this._getPrivateRenderSize(renderer, viewport)

    // 调整 RT 尺寸
    this.rtEDL!.setSize(width, height)

    // 清除 EDL RT
    this._bindFullTarget(renderer, this.rtEDL, width, height)
    renderer.setClearColor(0x000000, 0)
    renderer.state.buffers.depth.setClear(1)
    renderer.clear(true, true, true)

    // 设置材质参数
    for (const octree of octrees) {
      const material = octree.material
      material.weighted = false
      material.useEDL = true
      material.screenWidth = width
      material.screenHeight = height
    }

    // 渲染点云到 rtEDL
    for (const octree of octrees) {
      const visibleNodes = octree.visibleNodes
      if (visibleNodes.length === 0) continue

      this.pRenderer.renderOctree(octree, visibleNodes, camera, this.rtEDL, {
        transparent: false,
      })
    }

    renderer.resetState()
    this._bindViewOutput(renderer, outputTarget, viewport)

    // EDL 后处理 pass：输出到 outputTarget
    const uniforms = this.edlMaterial!.uniforms
    uniforms.screenWidth.value = width
    uniforms.screenHeight.value = height

    uniforms.uEDLColor.value = this.rtEDL!.texture
    uniforms.uEDLDepth.value = this.rtEDL!.depthTexture
    uniforms.edlStrength.value = config.edl.strength
    uniforms.radius.value = config.edl.radius
    uniforms.opacity.value = config.edl.opacity

    // 合成 shader 会在无点数据像素上 discard，需先显式清空输出目标，
    // 避免在 pipeline 全局 autoClear=false 时残留上一帧内容。
    this._clearCompositeOutput(renderer, outputTarget, viewport)

    // screenPass 渲染到 outputTarget（null 表示默认帧缓冲）
    if (outputTarget) {
      this._retainScreenPassRenderer(renderer)
      screenPass.render(renderer, this.edlMaterial!, outputTarget, viewport)
    } else {
      this._retainScreenPassRenderer(renderer)
      screenPass.render(renderer, this.edlMaterial!, undefined, viewport)
    }

    // RT 进出协议：恢复输出目标
    renderer.setRenderTarget(outputTarget)
  }

  // ─── HQ Splat 渲染路径 ────────────────────────────────

  /** 懒初始化 HQ Splat 资源 */
  private _initHQ(): void {
    if (this.normalizationMaterial) return

    this.normalizationMaterial = new NormalizationMaterial()
    this.normalizationMaterial.depthTest = false
    this.normalizationMaterial.depthWrite = false
    this.normalizationMaterial.transparent = true

    this.normalizationEDLMaterial = new NormalizationEDLMaterial()
    this.normalizationEDLMaterial.depthTest = false
    this.normalizationEDLMaterial.depthWrite = false
    this.normalizationEDLMaterial.transparent = true

    this.rtDepth = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthTexture: new THREE.DepthTexture(undefined, undefined, THREE.UnsignedIntType),
    })

    this.rtAttribute = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthTexture: this.rtDepth.depthTexture,
    })
  }

  /** HQ Splat 渲染：深度 pass → 属性 pass → 归一化 pass */
  private _renderHQSplat(
    frame: ViewFrameContext,
    ctx: PhaseRenderContext,
    config: PointCloudRenderConfig
  ): void {
    this._initHQ()

    const { renderer, outputTarget, viewport } = ctx
    const camera = frame.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
    const octrees = this.getVisibleOctrees()
    const { width, height } = this._getPrivateRenderSize(renderer, viewport)
    const gl = renderer.getContext() as WebGL2RenderingContext

    // 调整 RT 尺寸
    this.rtDepth!.setSize(width, height)
    this.rtAttribute!.setSize(width, height)

    // 清除私有 RT
    renderer.setClearColor(0x000000, 0)
    this._bindFullTarget(renderer, this.rtDepth, width, height)
    renderer.clear(true, true, true)
    this._bindFullTarget(renderer, this.rtAttribute, width, height)
    renderer.clear(true, true, true)

    // 缓存原始材质
    const originalMaterials = new Map<PointCloudOctree, PointCloudMaterial>()
    for (const octree of octrees) {
      originalMaterials.set(octree, octree.material)
      this._ensureHQMaterials(octree)
    }

    // ── 深度 pass ──
    this._renderDepthPass(octrees, originalMaterials, camera, width, height, renderer)

    // ── 属性 pass ──
    this._renderAttributePass(octrees, originalMaterials, camera, width, height, renderer, gl)

    // 恢复原始材质
    for (const [octree, material] of originalMaterials) {
      octree.material = material
    }

    renderer.resetState()

    // ── 归一化 pass ──
    this._renderNormalizationPass(config, width, height, renderer, outputTarget, viewport)

    // RT 进出协议：恢复输出目标
    renderer.setRenderTarget(outputTarget)
  }

  /** 确保八叉树拥有 HQ 所需的深度材质和属性材质 */
  private _ensureHQMaterials(octree: PointCloudOctree): void {
    if (!this.depthMaterials.has(octree)) {
      const depthMaterial = new PointCloudMaterial()
      depthMaterial.setDefine('depth_pass', '#define hq_depth_pass')
      depthMaterial.setDefine('use_edl', '#define use_edl')
      this.depthMaterials.set(octree, depthMaterial)
    }

    if (!this.attributeMaterials.has(octree)) {
      const attributeMaterial = new PointCloudMaterial()
      this.attributeMaterials.set(octree, attributeMaterial)
    }
  }

  /**
   * 将原始材质的公共字段同步到派生材质（深度/属性 pass 共用）
   */
  private _syncDerivedMaterial(
    derived: PointCloudMaterial,
    original: PointCloudMaterial,
    octree: PointCloudOctree,
    width: number,
    height: number
  ): void {
    // 基础渲染参数
    derived.size = original.size
    derived.minSize = original.minSize
    derived.uniforms.maxSize.value = original.uniforms.maxSize.value
    derived.pointSizeType = original.pointSizeType
    derived.screenWidth = width
    derived.screenHeight = height
    derived.shape = PointShape.CIRCLE

    // 可见性纹理
    derived.visibleNodesTexture = original.visibleNodesTexture
    derived.uniforms.visibleNodes.value = original.visibleNodesTexture

    // 几何参数
    const octreeSize = octree.pcoGeometry?.boundingBox?.getSize(new THREE.Vector3()).x ?? 0
    derived.uniforms.octreeSize.value = octreeSize
    derived.spacing = octree.pcoGeometry.spacing

    // 分类
    derived.classification = original.classification
    derived.uniforms.classificationLUT.value.image.data =
      original.uniforms.classificationLUT.value.image.data
    derived.classificationTexture.needsUpdate = true

    // 过滤
    derived.uniforms.uFilterReturnNumberRange.value =
      original.uniforms.uFilterReturnNumberRange.value
    derived.uniforms.uFilterNumberOfReturnsRange.value =
      original.uniforms.uFilterNumberOfReturnsRange.value
    derived.uniforms.uFilterGPSTimeClipRange.value = original.uniforms.uFilterGPSTimeClipRange.value
    derived.uniforms.uFilterPointSourceIDClipRange.value =
      original.uniforms.uFilterPointSourceIDClipRange.value

    // 裁切
    derived.clipTask = original.clipTask
    derived.clipMethod = original.clipMethod
    derived.setClipBoxes(original.clipBoxes)
    derived.setClipPolygons(original.clipPolygons, original.clipPolygons?.length ?? 0)
  }

  /** HQ 深度 pass：以深度材质渲染到 rtDepth */
  private _renderDepthPass(
    octrees: PointCloudOctree[],
    originalMaterials: Map<PointCloudOctree, PointCloudMaterial>,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    width: number,
    height: number,
    renderer: THREE.WebGLRenderer
  ): void {
    for (const octree of octrees) {
      const material = originalMaterials.get(octree)!
      const depthMaterial = this.depthMaterials.get(octree)!

      // 公共字段同步
      this._syncDerivedMaterial(depthMaterial, material, octree, width, height)

      // 深度 pass 特有覆盖
      depthMaterial.weighted = false

      octree.material = depthMaterial
    }

    this._bindFullTarget(renderer, this.rtDepth, width, height)

    // 渲染深度到 rtDepth
    for (const octree of octrees) {
      const visibleNodes = octree.visibleNodes
      if (visibleNodes.length === 0) continue

      this.pRenderer.renderOctree(octree, visibleNodes, camera, this.rtDepth!)
    }
  }

  /** HQ 属性 pass：以属性材质渲染到 rtAttribute（加法混合） */
  private _renderAttributePass(
    octrees: PointCloudOctree[],
    originalMaterials: Map<PointCloudOctree, PointCloudMaterial>,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    width: number,
    height: number,
    renderer: THREE.WebGLRenderer,
    gl: WebGL2RenderingContext
  ): void {
    for (const octree of octrees) {
      const material = originalMaterials.get(octree)!
      const attributeMaterial = this.attributeMaterials.get(octree)!

      // 公共字段同步
      this._syncDerivedMaterial(attributeMaterial, material, octree, width, height)

      // 属性 pass 特有覆盖
      attributeMaterial.weighted = true
      attributeMaterial.activeAttributeName = material.activeAttributeName

      // 颜色/高程/强度参数
      attributeMaterial.elevationGradientRepeat = material.elevationGradientRepeat
      attributeMaterial.elevationRange = material.elevationRange
      attributeMaterial.gradient = material.gradient
      attributeMaterial.matcap = material.matcap
      attributeMaterial.intensityRange = material.intensityRange
      attributeMaterial.intensityGamma = material.intensityGamma
      attributeMaterial.intensityContrast = material.intensityContrast
      attributeMaterial.intensityBrightness = material.intensityBrightness
      attributeMaterial.rgbGamma = material.rgbGamma
      attributeMaterial.rgbContrast = material.rgbContrast
      attributeMaterial.rgbBrightness = material.rgbBrightness

      // 权重参数
      attributeMaterial.weightRGB = material.weightRGB
      attributeMaterial.weightIntensity = material.weightIntensity
      attributeMaterial.weightElevation = material.weightElevation
      attributeMaterial.weightClassification = material.weightClassification
      attributeMaterial.weightReturnNumber = material.weightReturnNumber
      attributeMaterial.weightSourceID = material.weightSourceID

      // 颜色
      attributeMaterial.color = material.color

      octree.material = attributeMaterial
    }

    this._bindFullTarget(renderer, this.rtAttribute, width, height)

    // 渲染属性到 rtAttribute（加法混合，不写深度）
    for (const octree of octrees) {
      const visibleNodes = octree.visibleNodes
      if (visibleNodes.length === 0) continue

      this.pRenderer.renderOctree(octree, visibleNodes, camera, this.rtAttribute!, {
        blendFunc: [gl.SRC_ALPHA, gl.ONE],
        depthWrite: false,
      })
    }
  }

  /** HQ 归一化 pass：从 rtAttribute 归一化输出到 outputTarget */
  private _renderNormalizationPass(
    config: PointCloudRenderConfig,
    width: number,
    height: number,
    renderer: THREE.WebGLRenderer,
    outputTarget: THREE.WebGLRenderTarget | null,
    viewport: PhaseRenderContext['viewport']
  ): void {
    const useEDL = config.edl.enabled
    const normMaterial = useEDL ? this.normalizationEDLMaterial! : this.normalizationMaterial!

    if (useEDL) {
      normMaterial.uniforms.edlStrength.value = config.edl.strength
      normMaterial.uniforms.radius.value = config.edl.radius
      normMaterial.uniforms.opacity.value = config.edl.opacity
      normMaterial.uniforms.screenWidth.value = width
      normMaterial.uniforms.screenHeight.value = height
      normMaterial.uniforms.uEDLMap.value = this.rtDepth!.texture
    }

    normMaterial.uniforms.uWeightMap.value = this.rtAttribute!.texture
    normMaterial.uniforms.uDepthMap.value = this.rtAttribute!.depthTexture

    // 归一化/EDL 合成同样会 discard 空像素，需要先清空输出目标。
    this._clearCompositeOutput(renderer, outputTarget, viewport)

    // screenPass 渲染归一化结果到 outputTarget
    if (outputTarget) {
      this._retainScreenPassRenderer(renderer)
      screenPass.render(renderer, normMaterial, outputTarget, viewport)
    } else {
      this._retainScreenPassRenderer(renderer)
      screenPass.render(renderer, normMaterial, undefined, viewport)
    }
  }

  /** 为会 discard 空像素的全屏合成 pass 绑定并清空输出目标 */
  private _clearCompositeOutput(
    renderer: THREE.WebGLRenderer,
    outputTarget: THREE.WebGLRenderTarget | null,
    viewport: PhaseRenderContext['viewport']
  ): void {
    this._bindViewOutput(renderer, outputTarget, viewport)
    renderer.setClearColor(0x000000, 0)
    renderer.clear(true, true, true)
  }

  // ─── 资源释放 ──────────────────────────────────────────

  /** 释放 EDL 相关资源 */
  private _disposeEDLResources(): void {
    if (this.edlMaterial) {
      this.edlMaterial.dispose()
      this.edlMaterial = null
    }

    if (this.rtEDL) {
      this.rtEDL.texture?.dispose()
      this.rtEDL.depthTexture?.dispose()
      this.rtEDL.dispose()
      this.rtEDL = null
    }
  }

  /** 释放 HQ Splat 相关资源 */
  private _disposeHQResources(): void {
    if (this.normalizationMaterial) {
      this.normalizationMaterial.dispose()
      this.normalizationMaterial = null
    }

    if (this.normalizationEDLMaterial) {
      this.normalizationEDLMaterial.dispose()
      this.normalizationEDLMaterial = null
    }

    if (this.rtDepth) {
      this.rtDepth.texture?.dispose()
      this.rtDepth.depthTexture?.dispose()
      this.rtDepth.dispose()
      this.rtDepth = null
    }

    if (this.rtAttribute) {
      // depthTexture 与 rtDepth 共享，不重复释放
      this.rtAttribute.texture?.dispose()
      this.rtAttribute.dispose()
      this.rtAttribute = null
    }

    // 释放缓存的 HQ 材质
    for (const material of this.depthMaterials.values()) {
      material.dispose()
    }
    this.depthMaterials.clear()

    for (const material of this.attributeMaterials.values()) {
      material.dispose()
    }
    this.attributeMaterials.clear()
  }
}
